import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Html, Line } from "@react-three/drei";
import * as THREE from "three";
import { Player, Question } from "../../types";
import { UNITS_PER_STEP, LANE_SPACING, PLAYER_COLORS } from "./constants";
import { getAvatarAnchor } from "./avatarAnchors";

/*
 * Bulles de question sur l'écran spectateur.
 *
 * Pourquoi : l'écran spectateur montrait des avatars qui avancent à des vitesses
 * différentes sans qu'on sache pourquoi. Les questions n'étaient visibles que sur
 * les téléphones. Afficher la question en cours de chacun donne du sens à ce qu'on
 * regarde : on voit qui bute sur quoi, et qui répond vite.
 *
 * Deux difficultés, traitées séparément :
 *
 * 1. LE PLACEMENT. Les joueurs sont souvent collés les uns aux autres (départ,
 *    égalité), donc des bulles ancrées pile au-dessus de chaque avatar se
 *    recouvrent et deviennent illisibles. D'où l'algorithme de séparation
 *    ci-dessous : chaque bulle VISE l'abscisse de son avatar, puis on écarte les
 *    voisines jusqu'à respecter un écart minimum, et on alterne deux rangées.
 *
 * 2. LE FIL. Ce n'est pas un trait droit mais une corde de Verlet, attachée à la
 *    tête de l'avatar et à la bulle, avec gravité et contrainte de longueur. Elle
 *    pend, oscille quand l'avatar accélère, et les fils se croisent naturellement.
 */

// Trois rangées. La séparation horizontale ne joue qu'ENTRE bulles d'une même
// rangée, donc le nombre de rangées fixe le nombre de "colonnes" nécessaires :
// à 6 joueurs on tombe à 2 bulles par rangée au lieu de 3, chacune reste donc
// beaucoup plus près de son avatar.
// Hauteurs calees sur le cadrage de la camera spectateur : au-dela de ~17 unites
// la rangee du haut sort du champ et l'etiquette de nom se fait couper par le bord
// superieur (constate en capture a 19.6).
const ROW_HEIGHTS = [6.6, 11.8, 17.0];

// Toutes les bulles sur le MÊME plan Z, quel que soit le couloir du joueur.
// Avec le Z de leur couloir, la profondeur décalait leur hauteur apparente à
// l'écran et annulait la séparation en rangées : deux bulles de rangées
// différentes finissaient quand même l'une sur l'autre (constaté en capture).
// Le fil, lui, rejoint bien l'avatar dans son couloir.
const BUBBLE_Z = 0;

// Écart horizontal minimum entre deux bulles de la MÊME rangée, en unités monde.
// Calé sur la largeur apparente d'une bulle à la distance de la caméra spectateur.
const MIN_GAP_SAME_ROW = 13;

// Ressort du déplacement latéral. VOLONTAIREMENT sous-amorti (damping bien plus
// bas que la raideur) : la bulle dépasse sa cible puis revient, ce qui donne le
// rebond cartoon. Un simple lerp, comme avant, arrivait tout droit et faisait
// mort. À ne pas confondre avec le ressort de l'avatar (SPRING_DAMPING = 8), qui
// lui doit être quasi critique pour ne pas donner le mal de mer au joueur.
const BUBBLE_SPRING_STIFFNESS = 11;
const BUBBLE_SPRING_DAMPING = 3.4;

// Flottement d'inertie, facon ballon d'helium : la bulle n'est jamais tout a fait
// immobile. Amplitudes en unites monde, volontairement faibles.
const FLOAT_AMPLITUDE_Y = 0.42;
const FLOAT_AMPLITUDE_X = 0.3;
const FLOAT_SPEED_Y = 0.85;
const FLOAT_SPEED_X = 0.57;

// Corde : plus de segments = plus souple, mais coût linéaire par joueur.
const ROPE_SEGMENTS = 14;
const ROPE_GRAVITY = -7;
const ROPE_DAMPING = 0.93;
const ROPE_CONSTRAINT_PASSES = 6;

// Largeur IMPOSÉE. `maxWidth` ne suffit pas : le conteneur de drei <Html> est en
// position absolue sans largeur, la bulle se réduisait donc à son mot le plus long
// et s'étirait en colonne verticale illisible.
const BUBBLE_WIDTH = 300;

// Position calculée d'une bulle, partagée entre le calcul de layout et le rendu.
interface Slot {
  x: number;
  y: number;
  z: number;
  // Vitesses du ressort. Ce sont elles qui portent l'inertie : sans elles, pas de
  // depassement, donc pas de rebond.
  vx: number;
  vy: number;
}
type Layout = Map<string, Slot>;

/*
 * Calcule le placement de toutes les bulles, une fois par frame.
 *
 * Monté AVANT les bulles dans l'arbre : R3F exécute les useFrame dans l'ordre
 * d'enregistrement, le layout est donc à jour quand les bulles le lisent.
 */
function BubbleLayout({
  players,
  layout,
  rowOf,
}: {
  players: Player[];
  layout: Layout;
  // Rangee de chaque joueur, calculee une fois sur l'index de couloir (stable
  // pour toute la partie) et non recalculee a chaque frame.
  rowOf: Map<string, number>;
}) {
  useFrame((_, rawDelta) => {
    // Delta borne : un onglet remis au premier plan renvoie un delta enorme qui
    // ferait exploser le ressort.
    const delta = Math.min(rawDelta, 1 / 30);

    // Rangee ASSIGNEE PAR JOUEUR, et non deduite du classement par abscisse.
    //
    // C'etait la cause des animations erratiques : la rangee valait
    // `rang % nbRangees`, or le classement par X s'inverse a chaque frame entre
    // deux joueurs quasi a egalite. La bulle changeait donc de rangee plusieurs
    // fois par seconde et sautait verticalement. L'index de couloir, lui, ne bouge
    // pas de la partie.
    const rows: { id: string; x: number; row: number }[][] =
      ROW_HEIGHTS.map(() => []);

    players.forEach((p) => {
      // Position rendue et non la cible, sinon la bulle prend de l'avance sur son
      // propre avatar.
      const rendered = getAvatarAnchor(p.id);
      const x = rendered ? rendered.x : (p.position || 0) * UNITS_PER_STEP;
      const row = (rowOf.get(p.id) ?? 0) % ROW_HEIGHTS.length;
      rows[row].push({ id: p.id, x, row });
    });

    for (const row of rows) {
      if (row.length === 0) continue;
      // Dans une rangee, on trie par abscisse pour que l'ordre des bulles suive
      // celui des coureurs et que les fils ne se croisent pas inutilement. Le tri
      // est departage par id : deux joueurs exactement a egalite gardent un ordre
      // stable au lieu de permuter d'une frame a l'autre.
      row.sort((a, b) => (a.x - b.x) || (a.id < b.id ? -1 : 1));

      const desired = row.map((r) => r.x);
      // Separation : deux passes de relaxation entre voisins DE CETTE RANGEE.
      for (let pass = 0; pass < 2; pass++) {
        for (let i = 1; i < desired.length; i++) {
          if (desired[i] - desired[i - 1] < MIN_GAP_SAME_ROW) {
            desired[i] = desired[i - 1] + MIN_GAP_SAME_ROW;
          }
        }
        for (let i = desired.length - 2; i >= 0; i--) {
          if (desired[i + 1] - desired[i] < MIN_GAP_SAME_ROW) {
            desired[i] = desired[i + 1] - MIN_GAP_SAME_ROW;
          }
        }
      }

      row.forEach((r, i) => {
        const targetX = desired[i];
        const targetY = ROW_HEIGHTS[r.row];
        const previous = layout.get(r.id);
        if (!previous) {
          layout.set(r.id, { x: targetX, y: targetY, z: BUBBLE_Z, vx: 0, vy: 0 });
          return;
        }
        // Ressort sous-amorti sur X ET Y. Y etait pose sec : le moindre changement
        // de rangee tele-portait la bulle au lieu de l'y emmener.
        const dx = previous.x - targetX;
        previous.vx += (-BUBBLE_SPRING_STIFFNESS * dx - BUBBLE_SPRING_DAMPING * previous.vx) * delta;
        previous.x += previous.vx * delta;

        const dy = previous.y - targetY;
        previous.vy += (-BUBBLE_SPRING_STIFFNESS * dy - BUBBLE_SPRING_DAMPING * previous.vy) * delta;
        previous.y += previous.vy * delta;

        previous.z = BUBBLE_Z;
      });
    }

    // Un joueur qui a fini n'a plus de bulle : on libère son slot.
    const alive = new Set(players.map((p) => p.id));
    for (const id of [...layout.keys()]) if (!alive.has(id)) layout.delete(id);
  });

  return null;
}

interface BubbleProps {
  player: Player;
  laneIndex: number;
  totalLanes: number;
  question: string;
  questionIndex: number;
  color: string;
  layout: Layout;
}

function QuestionBubble({ player, laneIndex, totalLanes, question, questionIndex, color, layout }: BubbleProps) {
  // drei <Line> est une Line2 : sa géométrie est une LineGeometry, qui se met à
  // jour avec setPositions(Float32Array) et non setFromPoints(). C'est ce qui
  // faisait que le fil ne se dessinait pas du tout.
  const lineRef = useRef<{
    geometry: { setPositions: (a: ArrayLike<number>) => void };
    computeLineDistances?: () => void;
  } | null>(null);
  const htmlRef = useRef<THREE.Group>(null!);

  const lanesOffset = ((totalLanes - 1) * LANE_SPACING) / 2;
  const laneZ = -laneIndex * LANE_SPACING + lanesOffset;

  const rope = useMemo(() => {
    const pts: THREE.Vector3[] = [];
    const prev: THREE.Vector3[] = [];
    for (let i = 0; i < ROPE_SEGMENTS; i++) {
      const v = new THREE.Vector3();
      pts.push(v);
      prev.push(v.clone());
    }
    return { pts, prev, initialized: false };
  }, []);

  const linePoints = useMemo(
    () => Array.from({ length: ROPE_SEGMENTS }, () => new THREE.Vector3()),
    []
  );
  const flat = useMemo(() => new Float32Array(ROPE_SEGMENTS * 3), []);
  const anchor = useMemo(() => new THREE.Vector3(), []);
  const target = useMemo(() => new THREE.Vector3(), []);
  // Dephasage par joueur : sans lui les bulles flottent toutes a l'unisson et on
  // voit immediatement que c'est une boucle.
  const floatPhase = useMemo(() => Math.random() * Math.PI * 2, []);

  useFrame(({ clock }, rawDelta) => {
    // Delta borné : un onglet remis au premier plan après une pause renvoie un
    // delta énorme qui ferait exploser la corde.
    const delta = Math.min(rawDelta, 1 / 30);
    const t = clock.getElapsedTime();

    // Position REELLEMENT rendue de l'avatar, pas la cible serveur. Avec la cible,
    // la base du fil devancait le personnage de plusieurs unites pendant toute la
    // course du ressort : le fil semblait accroche a l'endroit ou il allait
    // arriver, pas a lui.
    const rendered = getAvatarAnchor(player.id);
    const headX = rendered ? rendered.x : (player.position || 0) * UNITS_PER_STEP;
    const headZ = rendered ? rendered.z : laneZ;
    anchor.set(headX, (rendered ? rendered.y : 0) + 2.2, headZ);
    const slot = layout.get(player.id);
    // Flottement ajoute au RENDU et non au layout : l'ecart minimum entre bulles
    // reste calcule sur des positions stables, sinon la separation oscillerait et
    // les bulles se pousseraient mutuellement en permanence.
    const floatY = Math.sin(t * FLOAT_SPEED_Y + floatPhase) * FLOAT_AMPLITUDE_Y;
    const floatX = Math.sin(t * FLOAT_SPEED_X + floatPhase * 1.7) * FLOAT_AMPLITUDE_X;
    if (slot) target.set(slot.x + floatX, slot.y + floatY, slot.z);
    else target.set(headX + floatX, ROW_HEIGHTS[0] + floatY, BUBBLE_Z);

    if (htmlRef.current) htmlRef.current.position.copy(target);

    if (!rope.initialized) {
      for (let i = 0; i < ROPE_SEGMENTS; i++) {
        rope.pts[i].lerpVectors(anchor, target, i / (ROPE_SEGMENTS - 1));
        rope.prev[i].copy(rope.pts[i]);
      }
      rope.initialized = true;
    }

    // Verlet : la position suivante se déduit du déplacement précédent, plus gravité.
    for (let i = 1; i < ROPE_SEGMENTS - 1; i++) {
      const p = rope.pts[i];
      const old = rope.prev[i];
      const vx = (p.x - old.x) * ROPE_DAMPING;
      const vy = (p.y - old.y) * ROPE_DAMPING;
      const vz = (p.z - old.z) * ROPE_DAMPING;
      old.copy(p);
      p.x += vx;
      p.y += vy + ROPE_GRAVITY * delta * delta * 60;
      p.z += vz;
    }

    rope.pts[0].copy(anchor);
    rope.prev[0].copy(anchor);
    rope.pts[ROPE_SEGMENTS - 1].copy(target);
    rope.prev[ROPE_SEGMENTS - 1].copy(target);

    // Contrainte de longueur, relaxée en plusieurs passes. Le segment visé est un
    // peu plus long que la distance directe divisée : la corde a du mou, elle pend.
    const segment = (anchor.distanceTo(target) / (ROPE_SEGMENTS - 1)) * 1.14;
    for (let pass = 0; pass < ROPE_CONSTRAINT_PASSES; pass++) {
      for (let i = 0; i < ROPE_SEGMENTS - 1; i++) {
        const a = rope.pts[i];
        const b = rope.pts[i + 1];
        const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
        const dist = Math.hypot(dx, dy, dz) || 1e-5;
        const diff = (dist - segment) / dist / 2;
        const ox = dx * diff, oy = dy * diff, oz = dz * diff;
        if (i !== 0) { a.x += ox; a.y += oy; a.z += oz; }
        if (i + 1 !== ROPE_SEGMENTS - 1) { b.x -= ox; b.y -= oy; b.z -= oz; }
      }
    }

    for (let i = 0; i < ROPE_SEGMENTS; i++) {
      flat[i * 3] = rope.pts[i].x;
      flat[i * 3 + 1] = rope.pts[i].y;
      flat[i * 3 + 2] = rope.pts[i].z;
    }
    const line = lineRef.current;
    if (line) {
      line.geometry.setPositions(flat);
      line.computeLineDistances?.();
    }
  });

  return (
    <group>
      <Line
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={lineRef as any}
        points={linePoints}
        color={color}
        lineWidth={5}
        transparent
        opacity={0.95}
      />
      <group ref={htmlRef}>
        {/* Pas de `center` : la bulle est ancrée par son BAS (translate -100% en Y),
            elle grandit donc vers le haut au lieu de s'étaler sur la piste quand la
            question est longue, et le fil la rejoint par sa pointe basse. */}
        <Html zIndexRange={[10, 0]} style={{ pointerEvents: "none" }}>
          {/* key = index de question : React remonte le noeud a chaque changement,
              ce qui rejoue l'animation CSS. Sans remontage, une animation deja
              terminee ne repart pas. */}
          <div
            key={questionIndex}
            style={{
              transform: "translate(-50%, -100%)",
              width: BUBBLE_WIDTH,
              animation: "bubble-pop 420ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              transformOrigin: "bottom center",
            }}
          >
            <div
              style={{
                position: "relative",
                fontFamily: "Nunito, sans-serif",
                background: "rgba(255,255,255,0.97)",
                border: `3px solid ${color}`,
                borderRadius: 20,
                padding: "16px 16px 13px",
                whiteSpace: "normal",
                boxShadow: `0 6px 0 0 ${color}55`,
                textAlign: "center",
              }}
            >
              {/* Nom en étiquette pleine, à cheval sur le bord haut : on sait d'un
                  coup d'œil à qui est la bulle, sans pastille séparée au-dessus de
                  l'avatar. */}
              <div
                style={{
                  position: "absolute",
                  top: -13,
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: color,
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: 13,
                  letterSpacing: "0.04em",
                  padding: "3px 14px",
                  borderRadius: 999,
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 0 0 rgba(46,26,59,0.18)",
                }}
              >
                {player.name}
              </div>
              <div style={{ color: "#2E1A3B", fontWeight: 800, fontSize: 15, lineHeight: 1.3 }}>
                {question}
              </div>
              {/* Pointe de la bulle vers le fil */}
              <div
                style={{
                  position: "absolute",
                  bottom: -9,
                  left: "50%",
                  transform: "translateX(-50%) rotate(45deg)",
                  width: 14,
                  height: 14,
                  background: "rgba(255,255,255,0.97)",
                  borderRight: `3px solid ${color}`,
                  borderBottom: `3px solid ${color}`,
                  borderBottomRightRadius: 4,
                }}
              />
            </div>
          </div>
        </Html>
      </group>
    </group>
  );
}

interface QuestionBubblesProps {
  players: Player[];
  questions: Question[];
  finishedPlayers: Set<string>;
}

export function QuestionBubbles({ players, questions, finishedPlayers }: QuestionBubblesProps) {
  const layout = useMemo<Layout>(() => new Map(), []);
  // Seuls les joueurs encore en course entrent dans le calcul : un joueur qui a
  // fini ne doit pas réserver un emplacement.
  const running = players.filter((p) => !finishedPlayers.has(p.id));

  // Rangee figee sur l'index de couloir, pas sur la position du moment : elle ne
  // doit jamais changer en cours de partie, sinon la bulle saute.
  const rowOf = useMemo(() => {
    const m = new Map<string, number>();
    players.forEach((p, i) => m.set(p.id, i % ROW_HEIGHTS.length));
    return m;
  }, [players]);

  return (
    <>
      <BubbleLayout players={running} layout={layout} rowOf={rowOf} />
      {running.map((player) => {
        const index = players.indexOf(player);
        const q = questions[player.questionIndex ?? 0];
        if (!q?.question) return null;
        return (
          <QuestionBubble
            key={player.id}
            player={player}
            laneIndex={index}
            totalLanes={players.length}
            question={q.question}
            questionIndex={player.questionIndex ?? 0}
            color={PLAYER_COLORS[index % PLAYER_COLORS.length]}
            layout={layout}
          />
        );
      })}
    </>
  );
}
